import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button } from "@air/primitive-button";
import { Form, Formik } from "formik";
import { memo, useCallback, useState } from "react";
import { AnonymousSignUpFooter } from "~/components/AnonymousSignUpForm/AnonymousSignUpFooter";
import { AnonymousSignUpInput } from "~/components/AnonymousSignUpForm/AnonymousSignUpInput";
import { AnonymousSignUpTermsInfo } from "~/components/AnonymousSignUpForm/AnonymousSignUpTermsInfo";
import { AnonymousLoginSchema, AnonymousSignUpSchema } from "~/components/AnonymousSignUpForm/types";
const initialValues = {
    email: "",
    name: ""
};
const validators = {
    login: AnonymousLoginSchema,
    signUp: AnonymousSignUpSchema
};
export const AnonymousSignUpForm = /*#__PURE__*/ memo((param)=>{
    let { title, description, onSignUp, onSubmitEmail, onFullLoginClick, onCancelClick, children } = param;
    const [signupStep, setSignupStep] = useState("login");
    const _onSubmit = useCallback(async (param)=>{
        let { email, name } = param;
        if (signupStep === "signUp") {
            await onSignUp(email, name);
        } else {
            try {
                const account = await onSubmitEmail(email);
                if (!account) {
                    setSignupStep("signUp");
                }
            } catch (_error) {
            // error thrown by onSubmitEmail - handled on top level
            }
        }
    }, [
        onSignUp,
        onSubmitEmail,
        signupStep
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": "ANONYMOUS_SIGN_UP_FORM",
        className: "flex flex-col gap-4 px-8 pb-4 pt-6",
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "text-24 font-semibold text-pigeon-700",
                children: title
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-16 text-pigeon-700",
                children: description
            }),
            /*#__PURE__*/ _jsx(Formik, {
                validateOnMount: true,
                initialValues: initialValues,
                onSubmit: _onSubmit,
                validationSchema: validators[signupStep],
                children: (param)=>{
                    let { isSubmitting, isValid, errors, touched, values } = param;
                    return /*#__PURE__*/ _jsxs(Form, {
                        className: "flex flex-col gap-5",
                        children: [
                            /*#__PURE__*/ _jsx(AnonymousSignUpInput, {
                                name: "email",
                                label: "Email",
                                error: touched.email ? errors.email : undefined
                            }),
                            signupStep === "signUp" && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(AnonymousSignUpInput, {
                                        name: "name",
                                        label: "Name",
                                        error: touched.name ? errors.name : undefined
                                    }),
                                    /*#__PURE__*/ _jsx(AnonymousSignUpTermsInfo, {})
                                ]
                            }),
                            children,
                            /*#__PURE__*/ _jsx(AnonymousSignUpFooter, {
                                onCancelClick: onCancelClick,
                                onFullLoginClick: onFullLoginClick,
                                className: "mt-4",
                                children: signupStep === "signUp" ? /*#__PURE__*/ _jsx(Button, {
                                    "data-testid": "ANONYMOUS_SIGN_UP_SUBMIT_BUTTON",
                                    type: "submit",
                                    appearance: "filled",
                                    color: "blue",
                                    size: "large",
                                    isLoading: isSubmitting,
                                    disabled: !isValid || !values.name,
                                    children: "Submit"
                                }) : /*#__PURE__*/ _jsx(Button, {
                                    "data-testid": "ANONYMOUS_SIGN_UP_CONTINUE_BUTTON",
                                    type: "submit",
                                    appearance: "filled",
                                    color: "blue",
                                    size: "large",
                                    isLoading: isSubmitting,
                                    disabled: !isValid,
                                    children: "Continue"
                                })
                            })
                        ]
                    });
                }
            })
        ]
    });
});
AnonymousSignUpForm.displayName = "AnonymousSignUpForm";
